/**
 * Version Utilities
 * 
 * Utility functions for comparing, sorting, and managing semantic version strings.
 * Supports alphanumeric comparison to correctly order versions like "1.9.0" < "1.10.0" < "2.0.0".
 */

/**
 * Compare two version strings alphanumerically.
 * 
 * Handles semantic versioning and pre-release identifiers (alpha, beta, rc).
 * Splits version strings by dots, dashes, and underscores, then compares
 * numeric segments numerically and string segments lexicographically.
 * 
 * @param v1 - First version string (e.g., "1.9.0", "2.0.0-beta")
 * @param v2 - Second version string to compare against
 * @returns -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 * 
 * @example
 * compareVersions("1.9.0", "1.10.0")   // => -1
 * compareVersions("2.0.0", "1.10.0")   // => 1
 * compareVersions("1.0", "1.0.0")      // => -1 (shorter is less)
 * compareVersions("1.0.0-beta", "1.0.0") // => -1 (pre-release is less)
 */
export function compareVersions(v1: string, v2: string): number {
  // Handle null/undefined
  if (!v1 && !v2) return 0;
  if (!v1) return -1;
  if (!v2) return 1;

  // Split versions by dots, dashes, and underscores
  // Convert numeric parts to numbers for proper numeric comparison
  const parts1 = v1.split(/[\.\-_]/).map(p => {
    const trimmed = p.trim();
    const num = parseInt(trimmed, 10);
    return isNaN(num) ? trimmed : num;
  });
  
  const parts2 = v2.split(/[\.\-_]/).map(p => {
    const trimmed = p.trim();
    const num = parseInt(trimmed, 10);
    return isNaN(num) ? trimmed : num;
  });
  
  const maxLength = Math.max(parts1.length, parts2.length);
  
  // Compare each segment
  for (let i = 0; i < maxLength; i++) {
    const part1 = parts1[i] ?? 0;
    const part2 = parts2[i] ?? 0;
    
    // Both are numbers - compare numerically
    if (typeof part1 === 'number' && typeof part2 === 'number') {
      if (part1 < part2) return -1;
      if (part1 > part2) return 1;
    } 
    // Both are strings - compare lexicographically
    else if (typeof part1 === 'string' && typeof part2 === 'string') {
      if (part1 < part2) return -1;
      if (part1 > part2) return 1;
    }
    // Mixed types - numbers are greater than strings (so "1.0.0" > "1.0.0-beta")
    else if (typeof part1 === 'number' && typeof part2 === 'string') {
      return 1;
    } else if (typeof part1 === 'string' && typeof part2 === 'number') {
      return -1;
    }
    // Handle default value (0) - string is treated as less than number
    else {
      const str1 = String(part1);
      const str2 = String(part2);
      if (str1 < str2) return -1;
      if (str1 > str2) return 1;
    }
  }
  
  return 0;
}

/**
 * Sort an array of version strings in descending order (highest version first).
 * 
 * Creates a new sorted array without modifying the original.
 * Uses alphanumeric comparison to ensure correct ordering.
 * 
 * @param versions - Array of version strings to sort
 * @returns New array sorted in descending order
 * 
 * @example
 * sortVersionsDescending(["1.0.0", "2.0.0", "1.10.0"])
 * // => ["2.0.0", "1.10.0", "1.0.0"]
 */
export function sortVersionsDescending(versions: string[]): string[] {
  if (!versions || versions.length === 0) {
    return [];
  }
  
  return [...versions].sort((a, b) => compareVersions(b, a));
}

/**
 * Sort an array of version strings in ascending order (lowest version first).
 * 
 * Creates a new sorted array without modifying the original.
 * Uses alphanumeric comparison to ensure correct ordering.
 * 
 * @param versions - Array of version strings to sort
 * @returns New array sorted in ascending order
 * 
 * @example
 * sortVersionsAscending(["1.0.0", "2.0.0", "1.10.0"])
 * // => ["1.0.0", "1.10.0", "2.0.0"]
 */
export function sortVersionsAscending(versions: string[]): string[] {
  if (!versions || versions.length === 0) {
    return [];
  }
  
  return [...versions].sort((a, b) => compareVersions(a, b));
}

/**
 * Get the maximum (latest/highest) version from an array of version strings.
 * 
 * @param versions - Array of version strings
 * @returns The highest version string, or undefined if array is empty
 * 
 * @example
 * getLatestVersion(["1.0.0", "2.0.0", "1.10.0"])
 * // => "2.0.0"
 */
export function getLatestVersion(versions: string[]): string | undefined {
  if (!versions || versions.length === 0) {
    return undefined;
  }
  
  return sortVersionsDescending(versions)[0];
}

/**
 * Get the minimum (oldest/lowest) version from an array of version strings.
 * 
 * @param versions - Array of version strings
 * @returns The lowest version string, or undefined if array is empty
 * 
 * @example
 * getOldestVersion(["1.0.0", "2.0.0", "1.10.0"])
 * // => "1.0.0"
 */
export function getOldestVersion(versions: string[]): string | undefined {
  if (!versions || versions.length === 0) {
    return undefined;
  }
  
  return sortVersionsAscending(versions)[0];
}

/**
 * Check if a version string is valid (basic validation).
 * 
 * @param version - Version string to validate
 * @returns true if version appears valid, false otherwise
 * 
 * @example
 * isValidVersion("1.0.0")      // => true
 * isValidVersion("1.0.0-beta") // => true
 * isValidVersion("invalid")    // => true (lenient validation)
 * isValidVersion("")           // => false
 */
export function isValidVersion(version: string): boolean {
  return !!version && version.trim().length > 0;
}
